import './App.css';
import { ReactNode } from 'react';
import Users from './pages/users';
import Teams from './pages/teams';
import Roles from './pages/roles';
import Matches from './pages/matches';
import Grounds from './pages/grounds';
import PageNotFound from './pages/404';
import Login from './pages/auth/login';
import Payments from './pages/payments';
import ViewUser from './pages/users/view';
import TeamView from './pages/teams/view';
import Dashboard from './pages/dashboard';
import SitePages from './pages/site-pages';
import PublicLayout from './layouts/public';
import MatchView from './pages/matches/view';
import Layout from './layouts/authenticated';
import Tournaments from './pages/tournaments';
import UpdateMatch from './pages/matches/edit';
import AdminProfile from './pages/admin/profile';
import CreateMatch from './pages/matches/create';
import PrivacyPolicy from './pages/privacy-policy';
import ContactSupport from './pages/contact-support';
import ViewTournament from './pages/tournaments/view';
import MatchCategories from './pages/match-categories';
import UpdateTournament from './pages/tournaments/edit';
import CreateTournament from './pages/tournaments/create';
import { Routes, Route, Navigate } from 'react-router-dom';
import TermsAndConditions from './pages/terms-and-conditions';
import AuthProvider, { useAuth } from './providers/auth-provider';
import SendNotification from './pages/notification/send-notification';
import BroadcastNotification from './pages/notification/broadcast';
import NotificationTemplates from './pages/notification/notification-templates';
import DeleteAccount from './pages/delete-account';
import MVPCalculation from './pages/mvp-calculation';
import NRRCalculation from './pages/nrr-calculation';
import AppLinks from './pages/app-links';
import OwnershipClaims from './pages/ownership-claims';
import DeletedAccounts from './pages/deleted-accounts';
import FeatureFlags from './pages/feature-flags';
import Homepage from './pages/homepage';
import LiveUsers from './pages/live-users';
import DeviceLogs from './pages/device-logs';
import MatchGrounds from './pages/match-grounds';
import CommunityListings from './pages/community-listings';
import MatchOps from './pages/match-ops';
import Moderation from './pages/moderation';
import User360 from './pages/user-360';
import Registrations from './pages/registrations';
import Orphaned from './pages/orphaned';
import OfficialsPage from './pages/officials';
import SportMonks from './pages/sportmonks';
import Tickers from './pages/tickers';

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { token } = useAuth();

  if (!token) {
    return <Navigate to="/login" />;
  }

  return <Layout>{children}</Layout>;
};

const App = () => {
  const { token } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={token ? <Navigate to="/" /> : <PublicLayout><Login /></PublicLayout>} />
      <Route path="/terms-and-conditions" element={<PublicLayout><TermsAndConditions /></PublicLayout>} />
      <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />

      {/* MVPCalculation */}
      <Route path="/mvp-calculation" element={<PublicLayout><MVPCalculation /></PublicLayout>} />
      {/* NRRCalculation */}
      <Route path="/nrr-calculation" element={<PublicLayout><NRRCalculation /></PublicLayout>} />

      {/* Delete Account */}
      <Route path="/delete-account" element={<PublicLayout><DeleteAccount /></PublicLayout>} />

      {/* Protected routes with authenticated layout */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AdminProfile /></ProtectedRoute>} />

      <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/users/view" element={<ProtectedRoute><ViewUser /></ProtectedRoute>} />

      {/* Matches */}
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/matches/create" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
      <Route path="/matches/edit" element={<ProtectedRoute><UpdateMatch /></ProtectedRoute>} />
      <Route path="/matches/view" element={<ProtectedRoute><MatchView /></ProtectedRoute>} />

      {/* Categories */}
      <Route path="/categories" element={<ProtectedRoute><MatchCategories /></ProtectedRoute>} />

      {/* Grounds */}
      <Route path="/grounds" element={<ProtectedRoute><Grounds /></ProtectedRoute>} />

      {/* Tournaments */}
      <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
      <Route path="/tournaments/create" element={<ProtectedRoute><CreateTournament /></ProtectedRoute>} />
      <Route path="/tournaments/edit" element={<ProtectedRoute><UpdateTournament /></ProtectedRoute>} />
      <Route path="/tournaments/view" element={<ProtectedRoute><ViewTournament /></ProtectedRoute>} />

      {/* Teams */}
      <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
      <Route path="/teams/view" element={<ProtectedRoute><TeamView /></ProtectedRoute>} />

      {/* Contacts and Support */}
      <Route path="/support" element={<ProtectedRoute><ContactSupport /></ProtectedRoute>} />

      {/* Payments */}
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />

      {/* Roles */}
      <Route path="/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />

      {/* App Links / Ownership Claims / Deleted Accounts */}
      <Route path="/app-links" element={<ProtectedRoute><AppLinks /></ProtectedRoute>} />
      <Route path="/ownership-claims" element={<ProtectedRoute><OwnershipClaims /></ProtectedRoute>} />
      <Route path="/deleted-accounts" element={<ProtectedRoute><DeletedAccounts /></ProtectedRoute>} />
      <Route path="/homepage" element={<ProtectedRoute><Homepage /></ProtectedRoute>} />
      <Route path="/feature-flags" element={<ProtectedRoute><FeatureFlags /></ProtectedRoute>} />
      <Route path="/live-users" element={<ProtectedRoute><LiveUsers /></ProtectedRoute>} />
      <Route path="/device-logs" element={<ProtectedRoute><DeviceLogs /></ProtectedRoute>} />
      <Route path="/match-grounds" element={<ProtectedRoute><MatchGrounds /></ProtectedRoute>} />
      <Route path="/community-listings" element={<ProtectedRoute><CommunityListings /></ProtectedRoute>} />
      <Route path="/match-ops" element={<ProtectedRoute><MatchOps /></ProtectedRoute>} />
      <Route path="/moderation" element={<ProtectedRoute><Moderation /></ProtectedRoute>} />
      <Route path="/user-360" element={<ProtectedRoute><User360 /></ProtectedRoute>} />
      <Route path="/registrations" element={<ProtectedRoute><Registrations /></ProtectedRoute>} />
      <Route path="/orphaned" element={<ProtectedRoute><Orphaned /></ProtectedRoute>} />
      <Route path="/officials" element={<ProtectedRoute><OfficialsPage /></ProtectedRoute>} />
      <Route path="/sportmonks" element={<ProtectedRoute><SportMonks /></ProtectedRoute>} />

      {/* Live Stream */}
      <Route path="/tickers" element={<ProtectedRoute><Tickers /></ProtectedRoute>} />

      {/* Site - Pages */}
      <Route path="/pages" element={<ProtectedRoute><SitePages /></ProtectedRoute>} />

      {/* Notification Routes (Email-Templates) */}
      <Route path="/notification-templates" element={<ProtectedRoute><NotificationTemplates /></ProtectedRoute>} />
      <Route path="/send-notification" element={<ProtectedRoute><SendNotification /></ProtectedRoute>} />
      <Route path="/broadcast-notification" element={<ProtectedRoute><BroadcastNotification /></ProtectedRoute>} />


      {/* Catch-all route */}
      <Route
        path="*"
        element={token ? <ProtectedRoute><PageNotFound /></ProtectedRoute> : <PageNotFound />}
      />
    </Routes>
  );
};

const AppWrapper = () => {
  return (
    <>
      <AuthProvider>
        <App />
      </AuthProvider>
    </>
  );
};

export default AppWrapper;